/**
 * Leaving-the-page decisions for the Report Builder editor (MDRS-555 / audit
 * RB-9).
 *
 * The autosave tick is debounced by 1500ms, so at any moment up to 1.5s of
 * typing exists only in the browser. Three ways out of the page drop it:
 * navigating to another part of the app (the provider lives in <Layout/> and
 * survives, but <DocxEditor> unmounts), hiding/closing the tab, and the
 * provider itself unmounting on logout.
 *
 * Pure predicates rather than inline conditions in the provider, for the same
 * reason `shouldSeedVersion` and `canEditWithLease` are: the provider's
 * lifetime is where this file's bugs live, and a boolean buried in an event
 * handler can only be tested by simulating the event.
 */

export interface LeaveState {
  /** The user has edited since the last successful save — `hasUserEditedRef`. */
  hasUnsavedEdits: boolean;
  /**
   * A save is running RIGHT NOW. Not the `saveDraft` mutation's `isPending`,
   * which is already false while the .docx upload is still in flight — the
   * window MDRS-554 was about. This spans the whole of `runSave`.
   */
  isSaveInFlight: boolean;
  /** The docx editor's document has no text (also true when it isn't mounted). */
  isEditorEmpty: boolean;
  /** A case + template are active; without them there is nothing to save. */
  hasActiveReport: boolean;
  /** This tab has been refused by the server and every further save is doomed. */
  hasConflict: boolean;
}

/**
 * Whether leaving now would lose work, and the browser should therefore ask
 * first.
 *
 * An in-flight save counts: `runSave` makes two versioned writes and the .docx
 * upload is the second, so a tab closed mid-save persists the text and loses
 * the document — exactly the "reload shows the older version" this ticket is
 * about.
 *
 * A conflict does NOT suppress the warning. It is the one state where the
 * pending edits cannot be rescued by a flush at all, which makes the prompt
 * more important, not less.
 *
 * An empty document does: the flush deliberately refuses to write a blank doc
 * over a real saved draft, so there is no pending work the user could keep by
 * staying.
 */
export const shouldWarnBeforeUnload = ({
  hasUnsavedEdits,
  isSaveInFlight,
  isEditorEmpty,
  hasActiveReport,
}: LeaveState): boolean => {
  if (!hasActiveReport) return false;
  if (isSaveInFlight) return true;
  return hasUnsavedEdits && !isEditorEmpty;
};

/**
 * Whether a leave event should run the debounced save immediately.
 *
 * Deliberately the same gates the autosave tick applies, so a flush can never
 * write something a tick wouldn't have. `isSaveInFlight` is absent on purpose:
 * saves are serialised through `createSaveChain`, so a flush queued behind a
 * running save is correct — dropping it would lose the very edit that arrived
 * during that save.
 */
export const shouldFlushPendingSave = ({
  hasUnsavedEdits,
  isEditorEmpty,
  hasActiveReport,
  hasConflict,
}: LeaveState): boolean =>
  hasActiveReport && hasUnsavedEdits && !isEditorEmpty && !hasConflict;

/**
 * Decide whether an in-app navigation away from the Report Builder should be
 * blocked (MDRS-555 / audit RB-9).
 *
 * `beforeunload` covers closing/reloading the tab, but the browser never fires
 * it for a client-side route change — which is how a doctor actually leaves the
 * Report Builder. Without this, the most common exit of all gets no protection.
 *
 * The important part is WHEN this runs: the router asks before it commits, so
 * <DocxEditor> is still mounted and its ProseMirror view still alive, and the
 * document can be captured normally. Waiting for an unmount instead means
 * racing the editor's own teardown — the vendor's `save()` resolves its buffer,
 * dispatches to the (by then destroyed) view, and its catch-all returns `null`,
 * discarding the document it had already built.
 *
 * `capture` resolves once the bytes are in hand, NOT once they are uploaded, so
 * the navigation is held only for local work. The happy path therefore shows
 * the user nothing at all. Blocking is reserved for the case where the document
 * could not be captured, because that is the only one where leaving loses work.
 */
export const resolveLeaveNavigation = async ({
  hasUnsavedWork,
  capture,
}: {
  hasUnsavedWork: () => boolean;
  capture: () => Promise<{ ok: boolean }>;
}): Promise<boolean> => {
  if (!hasUnsavedWork()) return false;
  try {
    const { ok } = await capture();
    return !ok;
  } catch {
    // A thrown capture is a failed capture. Falling through as "saved" would
    // deny the prompt to the exact case that needs it most.
    return true;
  }
};
